import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DataNoRowOverlayComponent, GroupDistributionAgg } from '@y42/ag-grid';
import { IListViewComponent, ListViewGridMessages, provideSimpleListView, QuickFilterModel } from '@y42/core/list-view';
import { TestResultStatus } from '@y42/data-test';
import { DateTimeFormat, DateTimeService } from '@y42/date-time';
import { importIcon } from '@y42/icons';
import { AbstractJobService } from '@y42/job';
import { ImportWriteType } from '@y42/json-schema';
import { ColDef, GridOptions, IAggFuncParams, ValueFormatterParams } from 'ag-grid-community';
import { DataTestJobGridHelperService } from '../../helpers/data-test-job-grid-helper.service';
import { JobWithDataTestsHttpModel, JobWithDataTestsUIModel } from '../../model/job.model';
import { DataTestColumnsCellRendererComponent } from '../cell-renderers/data-test-columns-cell-renderer/data-test-columns-cell-renderer.component';
import { DataTestConditionCellRendererComponent } from '../cell-renderers/data-test-condition-cell-renderer/data-test-condition-cell-renderer.component';
import { DataTestImportTypeCellRendererComponent } from '../cell-renderers/data-test-import-type-cell-renderer/data-test-import-type-cell-renderer.component';
import { DataTestResultCellRendererComponent } from '../cell-renderers/data-test-result-cell-renderer/data-test-result-cell-renderer.component';
import { DataTestTypeCellRendererComponent } from '../cell-renderers/data-test-type-cell-renderer/data-test-type-cell-renderer.component';
import { DataTestJobOverviewListViewService } from './data-test-job-overview-list-view.service';
import { DataTestJobOverviewListUIModel } from './data-test-job-overview-list.model';

@UntilDestroy()
@Component({
  selector: 'y42-data-test-job-overview-list',
  templateUrl: './data-test-job-overview-list.component.html',
  styleUrls: ['./data-test-job-overview-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideSimpleListView(DataTestJobOverviewListViewService), DataTestJobGridHelperService],
})
export class DataTestJobOverviewListComponent implements IListViewComponent {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly gridHelperService: DataTestJobGridHelperService,
    private readonly jobService: AbstractJobService<JobWithDataTestsHttpModel, JobWithDataTestsUIModel>,
  ) {}

  readonly selectedDateRangeForm = this.jobService.selectedDateRangeForm;
  readonly max = this.dateTimeService.endOfDay(new Date());

  readonly messages: ListViewGridMessages = {
    noDataHeader: `There are no Data Tests Jobs`,
    noDataImgSrc: 'assets/images/ag-grid-headers-icons/job.svg',
  };

  readonly gridOptions: GridOptions<DataTestJobOverviewListUIModel> = {
    noRowsOverlayComponent: 'dataNoRowOverlayComponent',
    rowSelection: 'multiple',
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    defaultColDef: {
      sortable: false,
      resizable: true,
      suppressMovable: true,
      suppressMenu: false,
    },
    animateRows: true,
    domLayout: 'autoHeight',
    autoGroupColumnDef: {
      headerName: 'Job ID',
      field: ColumnNames.request_id,
      maxWidth: 330,
      valueFormatter: (_: ValueFormatterParams<DataTestJobOverviewListUIModel, string>) => {
        return '';
      },
      aggFunc: 'minOfString',
    },
    suppressAggFuncInHeader: true,
    aggFuncs: {
      distribution: GroupDistributionAgg,
      minOfString: minOfStringAggregationFunction,
    },
  };

  readonly columnDefs: ColDef[] = [
    {
      headerName: 'Job ID',
      field: ColumnNames.request_id,
      hide: true,
      enableRowGroup: true,
      rowGroup: true,
    },
    {
      headerName: 'Job Mode',
      aggFunc: 'first',
      colId: ColumnNames.write_disposition,
      field: ColumnNames.write_disposition,
      cellRenderer: 'importTypeRenderer',
      maxWidth: 120,
      filter: 'agSetColumnFilter',
      getQuickFilterText: (params) => {
        if (params.value === ImportWriteType.Append) {
          return 'Incremental';
        } else {
          return 'Full';
        }
      },
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === ImportWriteType.Append) {
            return 'Incremental';
          } else {
            return 'Full';
          }
        },
      },
    },
    {
      headerName: 'Test',
      field: 'test.request.type',
      cellRenderer: 'dataTestTypeCellRenderer',
      aggFunc: 'count',
      maxWidth: 150,
    },
    {
      headerName: 'Started at',
      field: 'test.result.start_time',
      valueFormatter: (params: ValueFormatterParams<DataTestJobOverviewListUIModel, string>) => {
        return params.value ? this.dateTimeService.formatDate(params.value, DateTimeFormat.ShortDateTime) : '';
      },
      aggFunc: 'minOfString',
      maxWidth: 130,
      sortable: true,
      sort: 'desc',
    },
    {
      headerName: 'Duration',
      valueGetter: this.gridHelperService.durationValueGetter,
      valueFormatter: this.gridHelperService.durationValueFormatter,
      aggFunc: 'sum',
      maxWidth: 110,
      // todo tech-7874, create an aggregation function that calculates grouped duration by Hai's proposal
      // aggFunc: (params: IAggFuncParams<DataTestJobOverviewListUIModel, string>) => {
      //   // total duration = max(end_time) - min(start_time) (@hai.bui)
      //   // https://y42dotcom.slack.com/archives/C03H66MC1L2/p1661773771722989?thread_ts=1661770815.565369&cid=C03H66MC1L2)
      //
      //   return {
      //     value: 0,
      //     toString() {
      //       return `${this.value}`;
      //     },
      //   };
      // },
    },
    {
      headerName: 'Status',
      colId: 'status',
      field: 'test.result.status',
      aggFunc: 'distribution',
      cellRenderer: 'dataTestStatusCellRenderer',
      maxWidth: 100,
      filter: 'agSetColumnFilter',
      getQuickFilterText: this.gridHelperService.dataTestStatusValueFormatter,
      filterParams: {
        valueFormatter: this.gridHelperService.dataTestStatusValueFormatter,
      },
    },
    {
      headerName: 'Condition',
      field: 'test',
      cellRenderer: 'dataTestConditionCellRenderer',
      valueGetter: this.gridHelperService.conditionValueGetter,
      maxWidth: 200,
    },
    {
      headerName: 'Columns',
      field: 'test',
      cellRenderer: 'dataTestColumnsCellRenderer',
      valueGetter: this.gridHelperService.columnsValueGetter,
      cellRendererParams: {
        usePopupForColumnNames: true,
      },
    },
  ];

  readonly defaultQuickFilters: QuickFilterModel[] = [
    {
      id: 'failed_tests',
      default: false,
      name: 'Failed Tests',
      editable: false,
      filterModel: {
        status: {
          values: [TestResultStatus.Error],
          type: 'equals',
        },
      },
      is_font_awesome: true,
      icon: 'times',
      background_color: '#F36331',
    },
    {
      id: 'full_import',
      default: false,
      name: 'Full Imports',
      editable: false,
      filterModel: {
        write_disposition: {
          values: [ImportWriteType.Truncate],
          type: 'equals',
        },
      },
      is_font_awesome: false,
      icon: importIcon,
      background_color: '#f4f4fb',
    },
    {
      id: 'incremental_import',
      default: false,
      name: 'Incremental Imports',
      editable: false,
      filterModel: {
        write_disposition: {
          values: [ImportWriteType.Append],
          type: 'equals',
        },
      },
      is_font_awesome: false,
      icon: importIcon,
      background_color: '#f4f4fb',
    },
  ];

  readonly frameworkComponents: GridOptions['components'] = {
    dataNoRowOverlayComponent: DataNoRowOverlayComponent,
    dataTestTypeCellRenderer: DataTestTypeCellRendererComponent,
    dataTestConditionCellRenderer: DataTestConditionCellRendererComponent,
    dataTestColumnsCellRenderer: DataTestColumnsCellRendererComponent,
    importTypeRenderer: DataTestImportTypeCellRendererComponent,
    dataTestStatusCellRenderer: DataTestResultCellRendererComponent,
  };

  gridReady(options: GridOptions<unknown>) {
    options.api?.sizeColumnsToFit();
  }
}

const ColumnNames: Record<keyof DataTestJobOverviewListUIModel, keyof DataTestJobOverviewListUIModel> = {
  id: 'id',
  test: 'test',
  company_id: 'company_id',
  error: 'error',
  request_id: 'request_id',
  write_disposition: 'write_disposition',
};

function minOfStringAggregationFunction(params: IAggFuncParams<DataTestJobOverviewListUIModel, string>) {
  return [...params.values].sort((a, b) => a.localeCompare(b))[0] ?? '';
}
